// Worlds 2 and 3 here intentionally diverge from lib/game/tutorial_data.dart:
// that file's content predates a world reshuffle and still teaches one-way
// streets for world 2 and MST for world 3, while the current World model
// (src/models/world.ts) has world 2 = MST and world 3 = TSP. The content
// below matches the shipped world layout instead of the stale Dart source.

export interface TutorialCard {
  readonly title: string;
  readonly body: string;
}

export const WORLD_TUTORIALS: Readonly<Record<number, readonly TutorialCard[]>> = {
  1: [
    {
      title: 'Your Mission',
      body: 'Tap edges to build a path from the teal source node to the red sink node.',
    },
    {
      title: 'Watch the Cost',
      body: 'Each edge has a weight. Your total path cost is shown in the status bar — try to minimize it!',
    },
    {
      title: 'Submit & Stars',
      body: 'When your path reaches the destination, hit Submit. The closer to optimal, the more stars you earn.',
    },
  ],
  2: [
    {
      title: 'Connect Everything',
      body: 'Tap edges to add them to your tree. Connect all nodes using the minimum total weight.',
    },
    {
      title: 'No Cycles Allowed',
      body: "If an edge would create a loop, it can't be part of a spanning tree. Choose wisely!",
    },
    {
      title: 'Minimum Cost',
      body: 'You need exactly N−1 edges for N nodes. Pick the cheapest ones that keep everything connected.',
    },
  ],
  3: [
    {
      title: 'The Copper Rounds',
      body: 'Visit every substation exactly once and return to where you started — one closed tour, no detours.',
    },
    {
      title: 'Forced Legs First',
      body: 'A node with only two connections leaves you no choice — both of its edges must be in the tour. Start there.',
    },
    {
      title: 'Shortest Round',
      body: 'Every node ends up with exactly two tour edges. Among all valid tours, find the cheapest one.',
    },
  ],
  4: [
    {
      title: 'Pipe Network',
      body: 'Each pipe has a capacity. Trace a route from the source to the sink, pipe by pipe; when it reaches the sink, the valves open as far as the narrowest pipe allows.',
    },
    {
      title: 'Redirect the Pressure',
      body: 'Flow in equals flow out at every junction, so routes are all you need. Walk a route against a pipe that already carries flow to redirect it elsewhere.',
    },
    {
      title: 'Maximize Throughput',
      body: 'Push as much total flow from source to sink as possible. The pipes are your limit.',
    },
  ],
  5: [
    {
      title: 'Sever the Link',
      body: 'Tap edges to mark them for cutting. Your goal: disconnect the source from the sink.',
    },
    {
      title: 'Minimize the Cost',
      body: 'Each cut edge has a capacity cost. Find the cheapest set of cuts that severs all paths.',
    },
  ],
  6: [
    {
      title: 'Pair Them Up',
      body: 'Tap edges to match left-side nodes with right-side nodes. Each node can have at most one match.',
    },
    {
      title: 'Maximum Matching',
      body: "Find the largest possible set of matches. Greedy picks aren't always optimal!",
    },
  ],
  7: [
    {
      title: 'Sabotage',
      body: 'Tap edges to interdict them within your budget. The adversary will then find their best remaining path.',
    },
    {
      title: 'Strategic Cuts',
      body: 'Target the edges that hurt the most — force the longest possible detour for the adversary.',
    },
  ],
};

export function tutorialCardsForLevel(
  worldIndex: number,
  levelIndex: number,
): readonly TutorialCard[] | null {
  if (levelIndex !== 1) return null;
  return WORLD_TUTORIALS[worldIndex] ?? null;
}

export function tutorialId(worldIndex: number): string {
  return `tutorial_w${worldIndex}`;
}
